import React, { MouseEventHandler, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
    DeleteButton,
    GreenArrow,
    PlusIcon,
    TrashIcon,
} from "assets/images/index";
import ModalHeader from "components/BaseModalComponents/Header/ModalHeader";
import ModalFooter from "components/BaseModalComponents/Footer/ModalFooter";
import BasedModal, { IProps } from "../BaseModalComponents/BasedModal";
import {
    Button,
    ButtonContainer,
    ButtonsBlock,
    Container,
    Content,
    ModalBody,
    ParamName,
    Row,
    TypeSelect,
    Value,
} from "./filterModalStyles";
import {
    FilterButtons,
    LogicBlockItem,
    LogicSwitchBlock,
    WhiteButton,
} from "../BaseModalComponents/Footer/ModalFooterStyles";
import TickerParamsHandler, {
    FilterParam,
    FilterParamTypes,
} from "utils/ticker-params-handler";
import notifyError from "utils/Notifications/Error";
import { showModal } from "store/reducers/modal";
import { Styles } from "react-modal";
import {
    setFilterParamsForWorkspace,
    setLogic as SL,
    addFilterQuery,
    filterSection,
} from "store/reducers/FilterSlice";

export const logicBlocks: { text: string; key: Separator }[] = [
    {
        text: "И",
        key: "*",
    },
    {
        text: "ИЛИ",
        key: "+",
    },
];

const modalStyles: Styles = {
    overlay: {
        position: "fixed",
        zIndex: 100,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
    },
    content: {
        position: "static",
        display: "flex",
        flexDirection: "row",
        gap: "10px",
        width: "450px",
        padding: 0,
        boxShadow: "0px 0px 5px rgba(49, 69, 49, 0.14)",
        borderRadius: "10px",
    },
};

type FilterProps = {
    openAddParamsModal: MouseEventHandler;
    openFormulaModal: MouseEventHandler;
    openAreYouSureModal: MouseEventHandler;
    closeAreYouSureModal: MouseEventHandler;
    list: FilterParam[];
    onRemove: (param: any) => void;
    onRestoreFilter: ({ id }: { id: number | string }) => void;
    picked_tickers: string[];
    table_id: string | number;
    file_query?: string;
    manual_query?: string;
    logic: Separator;
};

type Separator = "*" | "+";

const scrollStyles = {
    backgroundColor: "#7CBE7B",
    width: "6px",
    borderRadius: "10px",
};

export function getOptions(type: keyof FilterParamTypes) {
    if (!type) {
        return [];
    }
    const filter_options = TickerParamsHandler.getFilterOptions();
    return filter_options[type].options;
}

function FilterModal({
    isOpen,
    title,
    onRequestClose,
    openAddParamsModal,
    openFormulaModal,
    openAreYouSureModal,
    style,
    list,
    onRemove,
    onRestoreFilter,
    picked_tickers,
    table_id,
    file_query,
    manual_query,
    logic,
}: IProps & FilterProps) {
    const [fileName, setFileName] = useState("");

    const dispatch = useDispatch();

    const [state, setState] = useState<{
        [key: string]: any;
        incorrect_filters: string[];
        list?: FilterParam[];
        manual_query: string;
    }>({
        incorrect_filters: [],
        manual_query: "",
    });

    useEffect(() => {
        setState((p) => ({
            ...p,
            list,
        }));
    }, [list]);

    useEffect(() => {
        setState((p) => ({
            ...p,
            manual_query,
        }));
    }, [manual_query]);

    const setLogic = (value: Separator) => {
        dispatch(SL({ id: table_id, value }));
    };

    const handleSwitchLogic = (value: Separator) => {
        setLogic(value);
    };

    const onInputChange = (
        e: React.FormEvent<HTMLInputElement>,
        param: FilterParam,
    ) => {
        const input = e.currentTarget.value;
        const check = TickerParamsHandler.checkInput(param.type, input);
        const incorrect_filters =
            input === "" || check === true
                ? state.incorrect_filters.filter((x) => x !== param.name)
                : Array.from(new Set([...state.incorrect_filters, param.name]));

        const list = Array.isArray(state.list)
            ? state.list.map((x) =>
                  x.name === param.name
                      ? {
                            ...x,
                            saved_filter_value: input,
                        }
                      : x,
              )
            : [
                  {
                      ...param,
                      saved_filter_value: input,
                  },
              ];
        setState((p) => ({
            ...p,
            list,
            [param.name]: {
                ...state[param.name],
                input: input,
            },
            incorrect_filters,
        }));
    };

    const handleOnRemove = (param: FilterParam) => {
        const new_state = Object.assign({}, state);
        new_state.list = new_state.list.filter((x) => x.name !== param.name);
        delete new_state[param.name];
        new_state.incorrect_filters = new_state.incorrect_filters.filter(
            (f) => f !== param.name,
        );
        setState(new_state);
        onRemove(param);

        const filters = (new_state.list || []).map((item) => ({
            name: item.name,
            option: item.saved_filter_option?.value,
            input: item.saved_filter_value,
        }));

        dispatch(
            filterSection({
                query: filters.length
                    ? TickerParamsHandler.processFilters(filters as any, logic)
                    : "",
                section_id: table_id,
            }),
        );
    };

    const onSelectChange = (
        value: React.FormEvent<HTMLSelectElement>,
        param: FilterParam,
    ) => {
        const full_opt = getOptions(param.type).find((x) => x.value === value);
        const list = Array.isArray(state.list)
            ? state.list.map((x) =>
                  x.name === param.name
                      ? {
                            ...x,
                            saved_filter_option: full_opt,
                        }
                      : x,
              )
            : [
                  {
                      ...param,
                      saved_filter_option: full_opt,
                  },
              ];
        setState((p) => ({
            ...p,
            [param.name]: {
                ...state[param.name],
                option: value,
            },
            list,
        }));
    };

    const handleApplyClick = () => {
        const filters = Object.keys(state)
            .filter(
                (k) =>
                    k !== "incorrect_filters" &&
                    k !== "list" &&
                    k !== "manual_query",
            )
            .map((k) => {
                if (state[k] && !Array.isArray(state[k])) {
                    return {
                        ...state[k],
                        name: k,
                    };
                }
            })
            .filter((x) => typeof x !== "undefined");

        const no_filters = !filters.length;
        const manual_formula =
            typeof state.manual_query === "string" &&
            state.manual_query.length > 0;
        const drop_filters = state.drop_filters === true;

        if (
            !manual_formula &&
            !drop_filters &&
            no_filters &&
            (!Array.isArray(list) || (Array.isArray(list) && !list.length))
        ) {
            notifyError("Ни указано ни одного фильтра");
            return;
        }
        /*
        if (!manual_formula && !drop_filters && filters.length !== list.length) {
            notifyError('Укажите значения фильтра');
            return;
        }
        */
        const exec = manual_formula
            ? ""
            : no_filters || drop_filters
              ? ""
              : TickerParamsHandler.processFilters(filters, logic);
        TickerParamsHandler.parseFormula(exec);
        const applied_filter_names = manual_formula ? undefined : state.list;

        if (applied_filter_names) {
            dispatch(
                setFilterParamsForWorkspace({
                    id: table_id,
                    filters: applied_filter_names,
                }),
            );
        }
        dispatch(
            showModal({
                modal_type: "are_you_sure",
                value: true,
                caller: table_id,
                args: {
                    title: "Вы точно хотите применить формулу?",
                    action: "apply_formula",
                    query: manual_formula ? manual_query : exec,
                    section_id: table_id,
                    applied_filter_names,
                },
            }),
        );
    };
    const saveParam = (name: string, option: string, value: string) => {
        const param = {
            name,
            option,
            input: value,
        };
        //@ts-ignore
        setState((p) => ({
            ...p,
            [name]: {
                input: value,
                option,
            },
            list: Array.isArray(state.list)
                ? state.list.some((x) => x.name === name)
                    ? //@ts-ignore
                      state.list
                    : //@ts-ignore
                      state.list.concat(param)
                : [param],
        }));
    };

    const _onRestoreHandler = () => {
        dispatch(
            showModal({
                modal_type: "are_you_sure",
                value: true,
                caller: table_id,
                args: {
                    title: "Вы точно хотите удалить фильтр?",
                    action: "clear_filters",
                    section_id: table_id,
                },
            }),
        );
    };

    return (
        <BasedModal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            style={modalStyles}
        >
            <Container>
                <ModalHeader>
                    <div>{title}</div>
                    <ButtonsBlock>
                        {fileName !== "" || (
                            <Button onClick={openAddParamsModal}>
                                <PlusIcon />
                            </Button>
                        )}
                        <Button onClick={_onRestoreHandler}>
                            <TrashIcon />
                        </Button>
                    </ButtonsBlock>
                </ModalHeader>
                <ModalBody
                    thumbSize={60}
                    renderThumbVertical={(props: any) => (
                        <div
                            {...props}
                            style={{
                                ...props.style,
                                ...scrollStyles,
                            }}
                        />
                    )}
                >
                    {state.manual_query && state.manual_query.length ? (
                        <Content>
                            {" "}
                            {`Следующая формула будет применена: `}
                            <span style={{ color: "#7CBE7B" }}>
                                {state.manual_query}
                            </span>
                        </Content>
                    ) : (
                        Array.isArray(state.list) &&
                        state.list.map((param) => {
                            if (
                                param.saved_filter_option &&
                                !state[param.name]
                            ) {
                                saveParam(
                                    param.name,
                                    param.saved_filter_option.value,
                                    param.saved_filter_value,
                                );
                            }
                            const sp = state[param.name];
                            return (
                                <Row key={param.name}>
                                    <Content>
                                        <ParamName>{param.name}</ParamName>
                                        <TypeSelect
                                            value={sp && sp.option}
                                            onChange={(value: any) =>
                                                onSelectChange(value, param)
                                            }
                                            suffixIcon={
                                                <GreenArrow className="ant-select-suffix" />
                                            }
                                            placeholder="Тип фильтрации"
                                            options={getOptions(param.type)}
                                        />
                                        <Value
                                            value={sp && sp.input}
                                            onChange={(e) =>
                                                onInputChange(e, param)
                                            }
                                            error={state.incorrect_filters.includes(
                                                param.name,
                                            )}
                                        />
                                        <ButtonContainer>
                                            <DeleteButton
                                                onClick={() =>
                                                    handleOnRemove(param)
                                                }
                                            />
                                        </ButtonContainer>
                                    </Content>
                                </Row>
                            );
                        })
                    )}
                </ModalBody>
                <ModalFooter
                    //disabled_apply_button={false}
                    onApplyClick={handleApplyClick}
                    applyText="Применить"
                    onCancelClick={onRequestClose}
                    cancelText="Отменить"
                    buttonBoxStyle={{
                        justifyContent: "space-between",
                        gap: 0,
                    }}
                >
                    <FilterButtons>
                        {true && (
                            <LogicSwitchBlock>
                                {logicBlocks.map((item, index) => (
                                    <LogicBlockItem
                                        onClick={() =>
                                            handleSwitchLogic(item.key)
                                        }
                                        key={item.key}
                                        isActive={(logic || "*") === item.key}
                                    >
                                        {item.text}
                                    </LogicBlockItem>
                                ))}
                            </LogicSwitchBlock>
                        )}
                        <WhiteButton
                            active={
                                typeof state.manual_query === "string" &&
                                state.manual_query !== ""
                            }
                            onClick={openFormulaModal}
                        >
                            Ввести вручную
                        </WhiteButton>
                    </FilterButtons>
                </ModalFooter>
            </Container>
        </BasedModal>
    );
}

export default FilterModal;
